import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Eye, MousePointerClick, BarChart3, Users, TrendingDown, Sparkles,
} from 'lucide-react';
import { marketingBlogAPI } from '../../api/api';
import KpiCard, { fmtNum, fmtPct, fmtPos } from '../../components/marketing/KpiCard';

const RANGOS = [
  { v: 7, label: '7 días' },
  { v: 30, label: '30 días' },
  { v: 90, label: '90 días' },
];

export default function MarketingBlogResumen() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    marketingBlogAPI
      .resumen(dias)
      .then((res) => { if (!cancelado) setData(res.data); })
      .catch((e) => {
        if (!cancelado) setError(e?.response?.data?.detail || 'Error cargando resumen');
      })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [dias]);

  if (loading) return <div className="text-center text-gray-500 py-12">Cargando resumen…</div>;
  if (error) return <div className="text-center text-red-600 py-12">{error}</div>;
  if (!data) return null;

  const t = data.totales;
  const d = data.deltas;

  return (
    <div className="space-y-6">
      {/* Selector de rango */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {RANGOS.map((r) => (
            <button
              key={r.v}
              onClick={() => setDias(r.v)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                dias === r.v ? 'bg-white text-duetto-primary shadow-sm font-semibold' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {data.desde} → {data.hasta} (vs. {data.comparativa_desde} → {data.comparativa_hasta})
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Posts publicados" value={fmtNum(t.posts_publicados)} icon={FileText} />
        <KpiCard label="Impresiones GSC" value={fmtNum(t.impressions)} icon={Eye} delta={d.impressions} />
        <KpiCard label="Clicks GSC" value={fmtNum(t.clicks)} icon={MousePointerClick} delta={d.clicks} />
        <KpiCard label="CTR" value={fmtPct(t.ctr)} icon={BarChart3} />
        <KpiCard label="Posición media" value={fmtPos(t.position_media)} icon={BarChart3} hint="GSC" />
        <KpiCard label="Sesiones GA4" value={fmtNum(t.sessions)} icon={Users} delta={d.sessions} hint="solo top 15/día" />
      </div>

      {/* Top 5 sesiones + Top 5 clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PanelTop
          titulo="Top 5 posts por sesiones"
          icon={Users}
          posts={data.top_por_sesiones}
          metricaLabel="sesiones"
          metricaKey="sessions"
          emptyMsg="Sin sesiones registradas en GA4 para /blog/* en este rango."
        />
        <PanelTop
          titulo="Top 5 posts por clicks (GSC)"
          icon={MousePointerClick}
          posts={data.top_por_clicks}
          metricaLabel="clicks"
          metricaKey="clicks"
          emptyMsg="Sin clicks registrados en GSC para /blog/* en este rango."
        />
      </div>

      {/* Posts en declive */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className="w-4 h-4 text-red-600" />
          <h3 className="font-semibold text-gray-900">Posts en declive</h3>
          <span className="text-xs text-gray-500">caída &gt;30% en impresiones vs. ventana anterior</span>
        </div>
        {data.en_declive.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            Ningún post con caída significativa en este rango.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2 font-medium">Post</th>
                  <th className="text-right py-2 px-2 font-medium">Imp. actual</th>
                  <th className="text-right py-2 px-2 font-medium">Imp. anterior</th>
                  <th className="text-right py-2 px-2 font-medium">Caída</th>
                </tr>
              </thead>
              <tbody>
                {data.en_declive.map((p) => (
                  <tr key={p.slug} className="border-b last:border-0">
                    <td className="py-2 px-2 text-gray-900">{p.title}</td>
                    <td className="py-2 px-2 text-right">{fmtNum(p.impresiones_actual)}</td>
                    <td className="py-2 px-2 text-right text-gray-500">{fmtNum(p.impresiones_anterior)}</td>
                    <td className="py-2 px-2 text-right font-semibold text-red-600">
                      -{(p.caida_pct * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cross-link a AI Visibility (ahora vive en Marketing > Web) */}
      <Link
        to="/marketing/web/ai-visibility"
        className="block bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 rounded-lg p-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-emerald-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-gray-900">¿Aparecemos en las IAs?</div>
            <p className="text-xs text-gray-600 mt-0.5">
              Visibilidad de Duetto en Gemini, GPT-4o y Claude — incluyendo citas a URLs <code className="px-1 bg-white/60 rounded text-[10px]">/blog/*</code>.
              Vive en <span className="font-medium">Marketing &gt; Web &gt; AI Visibility</span>.
            </p>
          </div>
          <span className="text-emerald-700 text-sm">Ver →</span>
        </div>
      </Link>
    </div>
  );
}

function PanelTop({ titulo, icon: Icon, posts, metricaLabel, metricaKey, emptyMsg }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-duetto-primary" />
        <h3 className="font-semibold text-gray-900">{titulo}</h3>
      </div>
      {posts.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">{emptyMsg}</p>
      ) : (
        <ol className="space-y-2">
          {posts.map((p, i) => (
            <li key={p.slug} className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0 mt-0.5">#{i + 1}</span>
                <span className="text-sm text-gray-900 truncate" title={p.title}>{p.title}</span>
              </div>
              <span className="text-sm font-semibold text-duetto-primary whitespace-nowrap">
                {fmtNum(p[metricaKey])} <span className="text-xs font-normal text-gray-500">{metricaLabel}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
